import { useState, useContext } from 'react'
import { ModalContext, ModalKey } from '../ModalContext'
import { eventPriorityOptions, GetSportTerrainOptions, GetSportDistanceOptions } from 'functions/eventUtils'
import moment from 'moment'

import BrandColorButton from 'components/Buttons/BrandColorButton'
import OutlineButton from 'components/Buttons/OutlineButton'

export default function SimulatorKinesisEventsModal() {

    const { modalData, ShowModal, HideModal } = useContext(ModalContext)

    const [kinesisEvents, setKinesisEvents] = useState(modalData.kinesisEvents ?? [])

    const GetEventDetailsText = (kinesisEvent) => {
        const sportText = kinesisEvent.sportKey ? kinesisEvent.sportKey.replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) : null

        const terrainText = kinesisEvent.eventTerrain === 'other'
            ? kinesisEvent.eventTerrainTextInput
            : GetSportTerrainOptions(kinesisEvent.sportKey).find((option) => option.key === kinesisEvent.eventTerrain)?.title

        const distanceText = kinesisEvent.eventDistance === 'other'
            ? kinesisEvent.eventDistanceTextInput
            : GetSportDistanceOptions(kinesisEvent.sportKey).find((option) => option.key === kinesisEvent.eventDistance)?.title

        return [sportText, terrainText, distanceText].filter((text) => text && text !== 'default-event').join(' · ')
    }

    const RemoveEvent = (eventId) => {
        const newKinesisEvents = kinesisEvents.filter((event) => event.id !== eventId)
        setKinesisEvents(newKinesisEvents)
        modalData.setKinesisEvents(newKinesisEvents)
    }

    const OnAddEventButton = () => {
        const addEventModalData = {
            key: ModalKey.KinesisEventV5,
            size: 'third',
            mode: 'create-simulator-event',
            kinesisEvents: kinesisEvents,
            SaveEvent: __SaveSimulatorEventModalAction
        }
        
        ShowModal(addEventModalData)
    }

    const OnEditEventButton = (kinesisEvent) => {
        const editEventModalData = {
            key: ModalKey.KinesisEventV5,
            size: 'third',
            mode: 'edit-simulator-event',
            kinesisEvent: kinesisEvent,
            kinesisEvents: kinesisEvents,
            SaveEvent: __EditSimulatorEventModalAction,
            DeleteEvent: () => __UpdateSimulatorEventsAndNavigateBack(kinesisEvents.filter((event) => event.id !== kinesisEvent.id))
        }

        ShowModal(editEventModalData)
    }

    const __SaveSimulatorEventModalAction = (newEvent) => {
        __UpdateSimulatorEventsAndNavigateBack([
            ...kinesisEvents,
            { ...newEvent, id: 'simulator-' + Date.now() }
        ])
    }

    const __EditSimulatorEventModalAction = (editedEvent) => {
        __UpdateSimulatorEventsAndNavigateBack(kinesisEvents.map((event) => event.id === editedEvent.id ? editedEvent : event))
    }

    const __UpdateSimulatorEventsAndNavigateBack = (newKinesisEvents) => {
        const sortedKinesisEvents = [...newKinesisEvents].sort((a, b) => a.eventDate.localeCompare(b.eventDate))

        modalData.setKinesisEvents(sortedKinesisEvents)

        ShowModal({ ...modalData, kinesisEvents: sortedKinesisEvents })
    }

    return (
        <div className='flex flex-col min-h-[min(36rem,80vh)] gap-y-4 text-left'>
            <div className='text-center text-xl font-semibold leading-6'>
                Events ({kinesisEvents.length})
            </div>

            <div className='space-y-2'>
                {kinesisEvents.length === 0 && (
                    <p className='pl-2 text-xs text-gray-500'>No events yet. Add an A race to define the program</p>
                )}

                {kinesisEvents.map((kinesisEvent) => {
                    const eventDetailsText = GetEventDetailsText(kinesisEvent)
                    const isARace = kinesisEvent.eventType === 'a-race'

                    return (
                        <div
                            key={kinesisEvent.id}
                            className='flex items-center gap-x-4 px-4 py-3 rounded-2xl bg-gray-100 cursor-pointer hover:bg-gray-200'
                            onClick={() => OnEditEventButton(kinesisEvent)}
                        >
                            <div className='w-10 h-10 flex-shrink-0 bg-orange-100 rounded-full flex items-center justify-center text-orange-400 text-lg font-bold'>
                                {kinesisEvent.eventType === 'a-race' ? 'A' : ''}
                                {kinesisEvent.eventType === 'b-race' ? 'B' : ''}
                                {kinesisEvent.eventType === 'c-race' ? 'C' : ''}
                            </div>

                            <div className='flex-1 min-w-0 space-y-0.5'>
                                <div className='flex items-center gap-x-2'>
                                    <p className='text-sm font-semibold truncate'>{kinesisEvent.name}</p>

                                    <p className='flex-shrink-0 text-xs text-gray-500'>
                                        {eventPriorityOptions.find((option) => option.key === kinesisEvent.eventType)?.label ?? kinesisEvent.eventType}
                                    </p>
                                </div>

                                {eventDetailsText !== '' && <p className='text-xs text-gray-600 truncate'>{eventDetailsText}</p>}

                                <p className='text-xs text-gray-500'>
                                    {isARace && 'Training ' + moment(kinesisEvent.eventTrainingStartDate).format('MMM D, YYYY') + ' → '}
                                    {'Event ' + moment(kinesisEvent.eventDate).format('MMM D, YYYY')}
                                    {isARace && ' (' + moment(kinesisEvent.eventDate).diff(moment(kinesisEvent.eventTrainingStartDate), 'weeks') + ' weeks)'}
                                    {isARace && kinesisEvent.deloadAfterEvent === 1 && ' + deload'}
                                </p>
                            </div>

                            <button
                                type='button'
                                className='flex-shrink-0 text-xs text-red-600 hover:underline'
                                onClick={(e) => {
                                    e.stopPropagation()
                                    RemoveEvent(kinesisEvent.id)
                                }}
                            >
                                Remove
                            </button>
                        </div>
                    )
                })}
            </div>

            <div className='mt-auto space-y-2'>
                <BrandColorButton
                    size="large"
                    text='Add Event'
                    action={OnAddEventButton}
                />

                <OutlineButton
                    size="large"
                    text='Done'
                    action={() => HideModal()}
                />
            </div>
        </div>
    )
}
